import clsx from "clsx";
import * as api from "../../api/statoLavori";
import { getErrorMessage } from "../../api/client";
import { getAttivitaSede } from "../../api/clients";
import { Device, getDevice } from "../../api/devices";
import { getSopralluogo } from "../../api/sopralluoghi";
import type { SopralluogoCollegato } from "../../components/ui/SopralluoghiCollegati";
import { TITOLO_BOZZA } from "./presentazione";

/**
 * La bozza dentro il form del modulo (issue #211, PR C2): «Conferma» apre il
 * form di Installazioni o di Attivazioni con `?new=1&bozza=<id>&pratica=<id>`
 * (`linkConfermaBozza`). Il form legge qui la bozza e i nomi da mostrare, si
 * precompila, e dopo il salvataggio la conferma col record creato. Un punto
 * solo per i due form, così la conferma funziona allo stesso modo nei due.
 *
 * La bozza può non essere più da confermare (confermata, scartata o superata
 * mentre il link era aperto): il form lo dice e si apre vuoto; il record si può
 * creare lo stesso, solo non risulta dalla bozza.
 */

/** Quello che serve al form per precompilarsi. */
export interface BozzaLetta {
  bozza: api.BozzaInElenco;
  /** La sede della bozza, con il suo cliente; NULL se non leggibile. */
  sede: { nome: string | null; indirizzo: string | null; citta: string | null; provincia: string | null; cliente: { id: string; nome: string } | null } | null;
  /** Il device della cassaforte, per la matricola; NULL se la cassaforte non ce l'ha o non è leggibile. */
  device: Device | null;
  /** Bozza installazione: il sopralluogo d'origine, per mostrarlo fra i collegati. */
  sopralluogo: SopralluogoCollegato | null;
}

export type StatoBozzaNelForm =
  | { stato: "caricamento"; bozzaId: string; praticaId: string | null }
  | { stato: "valida"; bozzaId: string; praticaId: string | null; letta: BozzaLetta }
  | { stato: "non_valida"; bozzaId: string; praticaId: string | null }
  | { stato: "errore"; bozzaId: string; praticaId: string | null; messaggio: string };

/**
 * La bozza da confermare e i nomi dei suoi id. NULL se la bozza non è più da
 * confermare. Sede e device mancanti non fermano niente: quei campi restano da
 * compilare a mano.
 */
export async function leggiBozzaPerForm(bozzaId: string): Promise<BozzaLetta | null> {
  const bozza = (await api.getBozze()).find((b) => b.id === bozzaId);
  if (!bozza) return null;
  const [attivita, device, sopralluogo] = await Promise.all([
    getAttivitaSede(bozza.dati.sede_id).catch(() => null),
    bozza.dati.device_id ? getDevice(bozza.dati.device_id).then((r) => r.record).catch(() => null) : Promise.resolve(null),
    bozza.dati.sopralluogo_id ? getSopralluogo(bozza.dati.sopralluogo_id).then((r) => r.record).catch(() => null) : Promise.resolve(null),
  ]);
  const s = attivita?.sede ?? null;
  return {
    bozza,
    sede: s
      ? {
          nome: s.nome_sede ?? null,
          indirizzo: s.indirizzo ?? null,
          citta: s.citta ?? null,
          provincia: s.provincia ?? null,
          cliente: s.cliente ? { id: s.cliente.id, nome: s.cliente.nome } : null,
        }
      : null,
    device,
    sopralluogo: sopralluogo
      ? { id: sopralluogo.id, insegna_cliente: sopralluogo.insegna_cliente, data_sopralluogo: sopralluogo.data_sopralluogo, stato: sopralluogo.stato }
      : null,
  };
}

/**
 * Dopo il salvataggio del record: conferma la bozza con il record creato.
 * NULL se è andata; altrimenti il messaggio da mostrare come avviso (il record
 * è salvato comunque; per esempio la bozza è stata scartata nel frattempo).
 */
export async function confermaDopoIlSalvataggio(bozzaId: string, recordId: string): Promise<string | null> {
  try {
    await api.confermaBozza(bozzaId, recordId);
    return null;
  } catch (err) {
    return getErrorMessage(err, "La conferma della bozza non è riuscita.");
  }
}

/** L'avviso in testa al form: da dove vengono i campi, o perché sono vuoti. */
export function AvvisoBozza({ stato }: { stato: StatoBozzaNelForm }) {
  const testo =
    stato.stato === "caricamento"
      ? "Carico la bozza…"
      : stato.stato === "valida"
        ? `Precompilato dalla ${TITOLO_BOZZA[stato.letta.bozza.tipo].toLowerCase()}: controlla i campi e salva. Salvando, la bozza risulta confermata.`
        : stato.stato === "non_valida"
          ? "La bozza non è più da confermare: forse è stata confermata, scartata o superata nel frattempo. Puoi creare il record lo stesso, ma non risulterà dalla bozza."
          : `Impossibile leggere la bozza: ${stato.messaggio} Il form resta da compilare a mano.`;
  return (
    <p
      role={stato.stato === "valida" || stato.stato === "caricamento" ? "status" : "alert"}
      className={clsx(
        "rounded-lg px-3 py-2 text-sm",
        stato.stato === "valida" || stato.stato === "caricamento"
          ? "bg-primary/5 text-ink"
          : stato.stato === "non_valida"
            ? "bg-amber-50 text-amber-900"
            : "bg-[#ef4444]/10 text-[#b91c1c]"
      )}
    >
      {testo}
    </p>
  );
}
